import React, { useEffect, useRef } from 'react'
import { useScrollProgress } from '../../../hooks/useParallax'
import { useScrollAnimation } from '../../../hooks/useScrollAnimation'
import styles from './CTASection.module.css'

/**
 * CTASection - Call to action before footer
 * Clean, focused section with animated wave background
 */
const CTASection = () => {
  const canvasRef = useRef(null)
  const animationRef = useRef(null)
  const { ref: sectionRef, progress, isInView } = useScrollProgress()
  const { ref: contentRef, isVisible } = useScrollAnimation({ threshold: 0.3 })

  // Animated gradient wave canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    const dpr = window.devicePixelRatio || 1

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`
      ctx.scale(dpr, dpr)
    }

    const animate = (time) => {
      const width = canvas.width / dpr
      const height = canvas.height / dpr

      ctx.clearRect(0, 0, width, height)

      // Draw multiple wave layers
      const waves = [
        { amplitude: 30, frequency: 0.008, speed: 0.0008, opacity: 0.08, yOffset: 0.6 },
        { amplitude: 25, frequency: 0.012, speed: 0.001, opacity: 0.06, yOffset: 0.65 },
        { amplitude: 20, frequency: 0.006, speed: 0.0006, opacity: 0.04, yOffset: 0.7 },
      ]

      waves.forEach((wave) => {
        ctx.beginPath()

        const baseY = height * wave.yOffset

        for (let x = 0; x <= width; x += 5) {
          const y = baseY +
            Math.sin(x * wave.frequency + time * wave.speed) * wave.amplitude +
            Math.sin(x * wave.frequency * 0.5 + time * wave.speed * 1.5) * wave.amplitude * 0.5

          if (x === 0) {
            ctx.moveTo(x, y)
          } else {
            ctx.lineTo(x, y)
          }
        }

        ctx.lineTo(width, height)
        ctx.lineTo(0, height)
        ctx.closePath()

        const gradient = ctx.createLinearGradient(0, baseY - wave.amplitude, 0, height)
        gradient.addColorStop(0, `rgba(16, 185, 129, ${wave.opacity})`)
        gradient.addColorStop(0.5, `rgba(20, 184, 166, ${wave.opacity * 0.7})`)
        gradient.addColorStop(1, 'rgba(16, 185, 129, 0)')

        ctx.fillStyle = gradient
        ctx.fill()
      })

      // Add some floating particles
      const particleCount = 15
      for (let i = 0; i < particleCount; i++) {
        const x = (width / particleCount) * i + Math.sin(time * 0.001 + i) * 30
        const y = height * 0.3 + Math.sin(time * 0.0008 + i * 2) * 50
        const radius = 2 + Math.sin(time * 0.002 + i) * 1
        const opacity = 0.2 + Math.sin(time * 0.001 + i) * 0.1

        ctx.beginPath()
        ctx.arc(x, y, radius, 0, Math.PI * 2)
        ctx.fillStyle = `rgba(16, 185, 129, ${opacity})`
        ctx.fill()
      }

      animationRef.current = requestAnimationFrame(animate)
    }

    resize()
    window.addEventListener('resize', resize)
    animationRef.current = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener('resize', resize)
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [])

  return (
    <section id="contact" className={styles.cta} ref={sectionRef}>
      {/* Animated wave canvas background */}
      <canvas ref={canvasRef} className={styles.waveCanvas} />

      {/* Animated gradient orbs */}
      <div
        className={styles.gradientOrb1}
        style={{
          transform: `translate(${progress * 30}px, ${progress * -20}px)`,
          opacity: isInView ? 0.6 : 0
        }}
      />
      <div
        className={styles.gradientOrb2}
        style={{
          transform: `translate(${progress * -20}px, ${progress * 30}px)`,
          opacity: isInView ? 0.4 : 0
        }}
      />

      <div className={styles.container}>
        <div
          ref={contentRef}
          className={styles.content}
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <h2 className={styles.title}>Let's Build Something Great</h2>
          <p className={styles.description}>
            Ready to scale your architecture, modernize legacy systems, or integrate AI into your platform?
          </p>
          <div className={styles.actions}>
            <a
              href="https://calendly.com/inonbaguio/30min"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.primaryBtn}
              data-cursor-hover
            >
              <span className={styles.btnText}>Book a Consultation</span>
              <span className={styles.btnIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </span>
              <span className={styles.btnShine} />
            </a>
            <a
              href="mailto:inonbaguio@gmail.com"
              className={styles.secondaryBtn}
              data-cursor-hover
            >
              Send an Email
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CTASection
